/**
 * Analytics page — standalone page for viewing site analytics.
 *
 * Features:
 * - Overview stats, timeseries, top pages/essays/referrers, devices
 * - Visitor journey matrix: users as rows, essays as columns
 * - Ability to label anonymous visitors with friendly names
 *
 * Only accessible to users with 'author' role.
 *
 * Called by:
 * - App.tsx as route for /reviewer/analytics
 */
import { useState } from 'react';
import { Typography, Alert, Tabs, Space } from 'antd';
import { BarChartOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import VisitorJourneyMatrix from '../components/admin/VisitorJourneyMatrix';

const { Title } = Typography;

function AnalyticsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('visitors');

  if (!user || user.role !== 'author') {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '48px 24px' }}>
        <Alert
          message="Access Denied"
          description="You do not have permission to access analytics. Only the author can access this page."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const tabItems = [
    {
      key: 'visitors',
      label: (
        <Space>
          <TeamOutlined />
          <span>Visitor Journeys</span>
        </Space>
      ),
      children: <VisitorJourneyMatrix />,
    },
    {
      key: 'overview',
      label: (
        <Space>
          <BarChartOutlined />
          <span>Overview</span>
        </Space>
      ),
      children: <AnalyticsDashboard />,
    },
  ];

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        Analytics
      </Title>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        style={{
          background: '#141414',
          padding: '16px',
          borderRadius: '8px',
        }}
      />
    </div>
  );
}

export default AnalyticsPage;
